import { c as _c } from "react/compiler-runtime";
import { watch } from "@arcgis/core/core/reactiveUtils";
import { WORLD_MAP_FOCUS_TILT, WORLD_MAP_FOCUS_ZOOM, WORLD_MAP_MAX_ZOOM, WORLD_MAP_MIN_ZOOM } from "@components/world-map/world-map-constants";
import { runGoTo } from "@components/world-map/world-map-utils";
import { WorldMapEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useRef, useState } from "react";

/** Camera tilt (degrees) at or below which the view is considered flat/top-down. */
const TILT_FLAT_THRESHOLD_DEG = 1;
/**
 * Provides map navigation callbacks: geolocation centering and zoom controls.
 * Shows a banner when geolocation permission is denied.
 */
export function useMapNavigation(viewRef, isViewReady, mapContext) {
  const $ = _c(37);
  const {
    trackEvent
  } = useTrackEvent();
  const [isLocationDisabled, setIsLocationDisabled] = useState(false);
  const [shouldShowLocationBanner, setShouldShowLocationBanner] = useState(false);
  const [isZoomOutDisabled, setIsZoomOutDisabled] = useState(false);
  const [isZoomInDisabled, setIsZoomInDisabled] = useState(false);
  const [canTilt, setCanTilt] = useState(false);
  const [isTilted, setIsTilted] = useState(false);
  const trackedLocationPermissionRef = useRef(null);
  const maxZoomRef = useRef(WORLD_MAP_MAX_ZOOM);
  let t0;
  let t1;
  if ($[0] !== isViewReady || $[1] !== viewRef) {
    t0 = () => {
      const view = viewRef.current;
      if (!isViewReady || !view) {
        return;
      }
      const updateButtonStates = function updateButtonStates() {
        if (!view) {
          return;
        }
        if (view.type !== "2d") {
          maxZoomRef.current = WORLD_MAP_MAX_ZOOM;
          setIsZoomOutDisabled(view.zoom <= WORLD_MAP_MIN_ZOOM);
          setIsZoomInDisabled(view.zoom >= WORLD_MAP_MAX_ZOOM);
          return;
        }
        const lods = view.constraints.effectiveLODs;
        const minZoom = lods ? Math.min(...lods.map(_temp)) : view.constraints.minZoom ?? 0;
        const maxZoom = lods ? Math.max(...lods.map(_temp2)) : view.constraints.maxZoom ?? WORLD_MAP_MAX_ZOOM;
        maxZoomRef.current = maxZoom;
        setIsZoomOutDisabled(view.zoom <= minZoom);
        setIsZoomInDisabled(view.zoom >= maxZoom);
      };
      updateButtonStates();
      const zoomHandle = watch(() => view.zoom, () => {
        updateButtonStates();
      });
      return () => {
        zoomHandle.remove();
      };
    };
    t1 = [viewRef, isViewReady];
    $[0] = isViewReady;
    $[1] = viewRef;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== mapContext || $[5] !== trackEvent) {
    t2 = isPermissionGranted => {
      if (trackedLocationPermissionRef.current === isPermissionGranted) {
        return;
      }
      trackedLocationPermissionRef.current = isPermissionGranted;
      trackEvent({
        name: WorldMapEvents.locationPermission,
        props: {
          isPermissionGranted,
          mapContext
        }
      });
    };
    $[4] = mapContext;
    $[5] = trackEvent;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const trackLocationPermission = t2;
  let t3;
  if ($[7] !== trackLocationPermission) {
    t3 = () => {
      trackLocationPermission(false);
      setIsLocationDisabled(true);
      setShouldShowLocationBanner(true);
    };
    $[7] = trackLocationPermission;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleLocationDenied = t3;
  let t4;
  if ($[9] !== trackLocationPermission) {
    t4 = () => {
      trackLocationPermission(true);
      setShouldShowLocationBanner(false);
    };
    $[9] = trackLocationPermission;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleLocationGranted = t4;
  let t5;
  if ($[11] !== handleLocationDenied || $[12] !== handleLocationGranted || $[13] !== viewRef) {
    t5 = () => {
      if (!navigator.geolocation) {
        return;
      }
      navigator.geolocation.getCurrentPosition(position => {
        handleLocationGranted();
        runGoTo(viewRef.current?.goTo({
          center: [position.coords.longitude, position.coords.latitude],
          zoom: WORLD_MAP_FOCUS_ZOOM
        }));
      }, error => {
        if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
          handleLocationDenied();
        }
      });
    };
    $[11] = handleLocationDenied;
    $[12] = handleLocationGranted;
    $[13] = viewRef;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const goToCurrentLocation = t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setShouldShowLocationBanner(false);
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const dismissLocationBanner = t6;
  let t7;
  let t8;
  if ($[16] !== isViewReady || $[17] !== viewRef) {
    t7 = () => {
      const view_0 = viewRef.current;
      if (!isViewReady || !view_0 || view_0.type !== "3d") {
        setCanTilt(false);
        return;
      }
      const sceneView = view_0;
      setCanTilt(true);
      const updateTilt = function updateTilt() {
        setIsTilted(sceneView.camera.tilt > TILT_FLAT_THRESHOLD_DEG);
      };
      updateTilt();
      const handle = watch(() => sceneView.camera.tilt, updateTilt);
      return () => handle.remove();
    };
    t8 = [viewRef, isViewReady];
    $[16] = isViewReady;
    $[17] = viewRef;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  let t9;
  if ($[20] !== viewRef) {
    t9 = () => {
      const view_1 = viewRef.current;
      if (!view_1 || view_1.type !== "3d") {
        return;
      }
      const nextTilt = view_1.camera.tilt > TILT_FLAT_THRESHOLD_DEG ? 0 : WORLD_MAP_FOCUS_TILT;
      runGoTo(view_1.goTo({
        center: view_1.center,
        tilt: nextTilt,
        heading: 0
      }));
    };
    $[20] = viewRef;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const toggleTilt = t9;
  let t10;
  if ($[22] !== viewRef) {
    t10 = () => {
      const view_2 = viewRef.current;
      if (view_2) {
        runGoTo(view_2.goTo({
          zoom: Math.min(view_2.zoom + 1, maxZoomRef.current)
        }));
      }
    };
    $[22] = viewRef;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const zoomIn = t10;
  let t11;
  if ($[24] !== viewRef) {
    t11 = () => {
      const view_3 = viewRef.current;
      if (view_3) {
        runGoTo(view_3.goTo({
          zoom: Math.max(view_3.zoom - 1, WORLD_MAP_MIN_ZOOM)
        }));
      }
    };
    $[24] = viewRef;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const zoomOut = t11;
  let t12;
  if ($[26] !== canTilt || $[27] !== goToCurrentLocation || $[28] !== isLocationDisabled || $[29] !== isTilted || $[30] !== isZoomInDisabled || $[31] !== isZoomOutDisabled || $[32] !== shouldShowLocationBanner || $[33] !== toggleTilt || $[34] !== zoomIn || $[35] !== zoomOut) {
    t12 = {
      goToCurrentLocation,
      zoomIn,
      zoomOut,
      isLocationDisabled,
      isZoomOutDisabled,
      isZoomInDisabled,
      shouldShowLocationBanner,
      dismissLocationBanner,
      canTilt,
      isTilted,
      toggleTilt
    };
    $[26] = canTilt;
    $[27] = goToCurrentLocation;
    $[28] = isLocationDisabled;
    $[29] = isTilted;
    $[30] = isZoomInDisabled;
    $[31] = isZoomOutDisabled;
    $[32] = shouldShowLocationBanner;
    $[33] = toggleTilt;
    $[34] = zoomIn;
    $[35] = zoomOut;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}
function _temp2(lod_0) {
  return lod_0.level;
}
function _temp(lod) {
  return lod.level;
}